<script lang="ts">
    /**
     * GridversePage - Gridverse 제품 소개 페이지 (/gridverse)
     *
     * 양대 스토어 정식 출시(2026-08-27). 히어로 + 게임 컨셉 3가지.
     * 컨셉 카피는 GV 세계관 문서 준수 (docs/gridverse/overview/):
     * - 차원 = 시즌제 1인 전용, 충돌은 차원격리 / 소셜은 차원초월(다른 차원 기지 방문)
     * - 전투 폐기, 점령✗→개척○, 대동굿 = 모든 차원이 통합되는 차원통합 토너먼트(정성 정산·명예의 전당 좌정)
     */
    import GridverseHeroSection from './sections/GridverseHeroSection.svelte';
    import GridverseShowcaseSection from './sections/GridverseShowcaseSection.svelte';

    let conceptVisible = $state(false);

    $effect(() => {
        const t = setTimeout(() => {
            conceptVisible = true;
        }, 150);
        return () => clearTimeout(t);
    });

    const concepts = [
        {
            icon: '🗺️',
            title: '걸어서 개척하는 영역',
            desc: '현실 공간을 직접 걸으며 기지를 짓고, 헥사 영역을 넓혀가요. 발걸음이 곧 성장이에요.',
        },
        {
            icon: '🌌',
            title: '나만의 차원',
            desc: '시즌마다 열리는 나만의 전용 차원에서 자유롭게 성장하고, 다른 차원의 기지를 방문하며 협력해요.',
        },
        {
            icon: '🎆',
            title: '정성이 모이는 대동굿',
            desc: '시즌의 끝엔 모든 차원이 하나로 통합되는 차원통합 토너먼트. 정성이 깃든 기지는 명예의 전당에 영원히 좌정해요.',
        },
    ];
</script>

<!-- title·description 은 config/seo.php 가 SSOT (LandingApp.applyMeta 참고) -->

<div class="gridverse-page">
    <GridverseHeroSection />

    <section id="gv-concept" class="concept-section">
        <div class="concept-inner" class:visible={conceptVisible}>
            <div class="concept-head">
                <h2 class="concept-title">Gridverse는 이런 게임이에요</h2>
                <p class="concept-subtitle">현실과 게임이 겹쳐지는 새로운 모험</p>
            </div>

            <div class="concept-grid">
                {#each concepts as c}
                    <div class="concept-card">
                        <span class="concept-emoji">{c.icon}</span>
                        <h3 class="concept-card-title">{c.title}</h3>
                        <p class="concept-card-desc">{c.desc}</p>
                    </div>
                {/each}
            </div>
        </div>
    </section>

    <GridverseShowcaseSection />
</div>

<style>
    .gridverse-page {
        width: 100%;
    }

    .concept-section {
        padding: 5rem 1.5rem;
        background: var(--landing-bg, #ffffff);
    }

    .concept-inner {
        max-width: 72rem;
        margin: 0 auto;
        opacity: 0;
        transform: translateY(20px);
        transition: all 0.8s cubic-bezier(0.16, 1, 0.3, 1);
    }

    .concept-inner.visible {
        opacity: 1;
        transform: translateY(0);
    }

    .concept-head {
        text-align: center;
        margin-bottom: 3rem;
    }

    .concept-title {
        font-size: clamp(1.8rem, 4vw, 2.5rem);
        font-weight: 700;
        letter-spacing: -0.02em;
        color: var(--landing-text, #1a1a1a);
        margin: 0 0 0.75rem;
    }

    .concept-subtitle {
        font-size: 1.05rem;
        color: var(--landing-text-sub, #666666);
        margin: 0;
    }

    .concept-grid {
        display: grid;
        grid-template-columns: 1fr;
        gap: 1.5rem;
        max-width: 60rem;
        margin: 0 auto;
    }

    .concept-card {
        background: var(--landing-bg-alt, #f8f9fa);
        border: 1px solid rgba(0, 0, 0, 0.05);
        border-radius: 20px;
        padding: 2rem;
        text-align: center;
        transition:
            transform 0.3s cubic-bezier(0.16, 1, 0.3, 1),
            box-shadow 0.3s ease;
    }

    .concept-card:hover {
        transform: translateY(-4px);
        box-shadow: 0 12px 30px rgba(0, 0, 0, 0.07);
    }

    .concept-emoji {
        display: inline-block;
        font-size: 2.5rem;
        line-height: 1;
        margin-bottom: 1rem;
    }

    .concept-card-title {
        font-size: 1.2rem;
        font-weight: 700;
        color: var(--landing-text, #1a1a1a);
        margin: 0 0 0.6rem;
    }

    .concept-card-desc {
        font-size: 0.95rem;
        line-height: 1.65;
        color: var(--landing-text-sub, #666666);
        margin: 0;
    }

    .concept-back {
        display: flex;
        justify-content: center;
        margin-top: 3rem;
    }

    .concept-back-link {
        display: inline-flex;
        align-items: center;
        gap: 0.4rem;
        color: var(--landing-text-sub, #666666);
        font-size: 0.95rem;
        font-weight: 500;
        text-decoration: none;
        transition:
            color 0.2s,
            gap 0.2s;
    }

    .concept-back-link:hover {
        color: var(--landing-primary, #1e5c3a);
        gap: 0.6rem;
    }

    @media (min-width: 768px) {
        .concept-section {
            padding: 6rem 2.5rem;
        }

        .concept-grid {
            grid-template-columns: repeat(3, 1fr);
            gap: 1.75rem;
        }
    }

    @media (min-width: 1024px) {
        .concept-section {
            padding: 7rem 3rem;
        }
    }
</style>
